import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiSearch,
  FiCheck,
  FiX,
  FiEye,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";
import { CiImageOn } from "react-icons/ci";
import { submissionApi, qc_status, seriesQcStatus } from "../../api/HttpApis";
import { showToast } from "../../utils/Toastify/Toastify";
import { useRbac } from "../../rbac/RbacContext";
import RejectModal from "./RejectModal";
import ConfirmDialog from "./ConfirmDialog";
import style from "./Submissions.module.css";
import { plural } from "./submissionUtils";
import useUrlTab from "../../hooks/useUrlTab";

const TABS = ["Pending", "Approved", "Rejected", "All"];
const PAGE_SIZES = [8, 16, 24];

// "36d waiting" style relative label from the submission date.
const waitingLabel = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  const days = Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
  if (days === 0) return "today";
  return `${days}d waiting`;
};

const fmtSubmitted = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const badgeClass = (qc) =>
  qc === "Approved" ? style.approved : qc === "Rejected" ? style.rejected : style.pending;

// A series group's id can equal a lone item's id, so rows are keyed by kind.
const rowKey = (item) =>
  item.seriesId
    ? `series:${item.seriesId}:${item.seasonNumber ?? "all"}:${item.id}`
    : `item:${item.id}`;

// Season rows of the same series go out as one series request (one email,
// no parallel writes to the same series); every other row is sent alone.
export const groupForSending = (list) => {
  const groups = [];
  const bySeries = new Map();
  list.forEach((row) => {
    if (!row.seriesId) {
      groups.push({ row, rows: [row] });
      return;
    }
    const existing = bySeries.get(row.seriesId);
    if (existing) {
      existing.rows.push(row);
      return;
    }
    const group = { row, rows: [row] };
    bySeries.set(row.seriesId, group);
    groups.push(group);
  });
  return groups;
};

// A series group is decided through the series endpoint (one email for the
// whole series), limited to the episodes the row stood for when the list
// loaded, so one uploaded since is never decided unseen. Anything else goes
// through the per-item endpoint.
export const sendDecision = ({ row, rows: groupRows }, payload) => {
  if (!row.seriesId) return qc_status(row.id, payload);
  if (groupRows.some((r) => !r.episodeIds.length)) {
    // Never send a series decision without ids: the API would then decide
    // every pending episode of the series.
    const err = new Error("Series row without episode ids");
    err.userMessage = `Couldn't confirm which episodes of “${row.name}” to decide. Open it to review.`;
    return Promise.reject(err);
  }
  return seriesQcStatus(row.seriesId, {
    ...payload,
    episode_ids: groupRows.flatMap((r) => r.episodeIds),
  });
};

function Thumb({ src, alt }) {
  const [err, setErr] = useState(false);
  if (!src || err)
    return (
      <div className={style.thumb}>
        <CiImageOn size={22} />
      </div>
    );
  return (
    <div className={style.thumb}>
      <img src={src} alt={alt || ""} onError={() => setErr(true)} />
    </div>
  );
}

const Submissions = () => {
  const navigate = useNavigate();
  const { can } = useRbac();
  const canDecide = can("content", "approve");

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({ All: 0, Pending: 0, Approved: 0, Rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null); // per-row action lock

  const [tab, setTab] = useUrlTab(TABS, "Pending");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("All");
  const [creatorFilter, setCreatorFilter] = useState("All");
  const [sort, setSort] = useState("oldest");

  // rowKey -> row, so a bulk selection spanning pages still has each row as the
  // admin saw it (a series group is decided differently from a lone item).
  const [selected, setSelected] = useState(() => new Map());
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);

  // Dropdown options (Type / Creator) come from a one-time facet fetch so they
  // list ALL values, not just those on the current server page.
  const [contentTypes, setContentTypes] = useState(["All"]);
  const [creators, setCreators] = useState(["All"]);

  // reject modal state — { rows, title } or null
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejecting, setRejecting] = useState(false);

  // approve confirm state — { rows, title } or null
  const [approveTarget, setApproveTarget] = useState(null);

  const mapRow = (item) => ({
    id: item.id,
    name: item.name || "Untitled",
    media: item.portrait,
    creator: item.creator?.name || "Unknown",
    // The full creator object too: the takedown dialog needs to know whether
    // this creator publishes without review, which the flattened display
    // name above cannot carry.
    creatorInfo: item.creator || null,
    contentType: item["content-type"] || "—",
    qcStatus: item.qc_status || "Pending",
    submittedAt: item.submission_date || item.created_at || null,
    // Several episodes of one series, reviewed and decided as one row.
    //
    // This repo's backend marks the group with `group_type: 'series'` and puts
    // the series id in `id` (VideoController.submissions); upstream's emits
    // `type: 'series_group'` + a separate `series_id`. Both shapes are read so
    // the row works against either, rather than silently producing seriesId:
    // null -- which would disable the drill-down, the grouped title and the
    // seriesQcStatus bulk decision for every row.
    seriesId:
      item.group_type === "series"
        ? item.series_id ?? item.id
        : item.type === "series_group"
          ? item.series_id
          : null,
    // The ids are what gets decided, so they are the count when present.
    episodeCount: Array.isArray(item.episode_ids)
      ? item.episode_ids.length
      : item.episode_count || 0,
    // The exact episodes the row stands for, as loaded: only these are decided.
    episodeIds: Array.isArray(item.episode_ids) ? item.episode_ids : [],
    seasonNumber: item.season_number ?? null,
    seasonName: item.season_name || null,
    // A lone episode: its series / season / episode number for the subtext.
    seriesName: item.series_name || null,
    episodeNumber: item.episode_number ?? null,
  });

  const episodeContext = (item) =>
    [
      item.seriesName,
      item.seasonNumber != null ? `Season ${item.seasonNumber}` : null,
      item.episodeNumber != null ? `Episode ${item.episodeNumber}` : null,
    ]
      .filter(Boolean)
      .join(" · ");

  // A group from one season (e.g. a new season of an existing series) is
  // titled by the season; the series name moves to the subtext.
  const rowTitle = (item) => {
    if (!item.seriesId || item.seasonNumber == null) return item.name;
    const label = `Season ${item.seasonNumber}`;
    const extra =
      item.seasonName && item.seasonName.trim().toLowerCase() !== label.toLowerCase()
        ? ` · ${item.seasonName}`
        : "";
    return label + extra;
  };
  const dialogTitle = (item) =>
    item.seriesId && item.seasonNumber != null ? `${item.name} · ${rowTitle(item)}` : item.name;

  const errorMessage = (err, fallback) =>
    err?.userMessage || err?.response?.data?.message || fallback;

  // Each row is sent and settles on its own, so a bulk action that partly
  // fails still reports and refreshes what did go through, leaving only the
  // failed rows selected for a retry. When nothing went through, the dialog
  // stays open for a retry.
  const applyDecision = async (list, payload, { verb, fallback, success, close }) => {
    const groups = groupForSending(list);
    const results = await Promise.allSettled(groups.map((g) => sendDecision(g, payload)));
    const done = [];
    const failed = [];
    const failedRows = [];
    results.forEach((r, i) => {
      if (r.status === "fulfilled") {
        done.push(r.value);
      } else {
        failed.push(r.reason);
        failedRows.push(...groups[i].rows);
      }
    });
    if (!done.length) {
      showToast(errorMessage(failed[0], fallback), "error");
      return;
    }
    showToast(
      failed.length
        ? `${list.length - failedRows.length} of ${list.length} ${verb}; ${failedRows.length} failed: ${errorMessage(failed[0], fallback)}`
        : success(done),
      failed.length ? "error" : "success"
    );
    close();
    setSelected(new Map(failedRows.map((r) => [rowKey(r), r])));
    const fresh = await load();
    // Keep a failed row selected only if it is still listed, and as reloaded:
    // a stale copy could be invisible (unchecked) yet still counted and sent.
    if (fresh) {
      const byKey = new Map(fresh.map((r) => [rowKey(r), r]));
      setSelected(
        new Map(
          failedRows
            .filter((r) => byKey.has(rowKey(r)))
            .map((r) => [rowKey(r), byKey.get(rowKey(r))])
        )
      );
    }
  };

  // Debounce the search box so we don't hit the server on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  // Server-side fetch: filters, sort, and pagination all happen on the backend.
  // The numeric-ID shortcut is applied client-side after fetch (rare, cheap).
  const load = async () => {
    setLoading(true);
    try {
      const filters = {};
      if (debouncedSearch.trim()) filters.search = debouncedSearch.trim();
      if (tab !== "All") filters.qc_status = tab;
      if (typeFilter !== "All") filters.content_type = typeFilter;
      if (creatorFilter !== "All") filters.creator = creatorFilter;
      if (sort) filters.sort = sort;

      const res = await submissionApi(filters, pageSize, page);
      const data = Array.isArray(res?.data?.data) ? res.data.data : [];
      const mapped = data.map(mapRow);
      setRows(mapped);
      setTotal(res?.data?.total || 0);
      if (res?.data?.counts) setCounts(res.data.counts);
      return mapped;
    } catch (e) {
      console.error("Failed to fetch submissions:", e);
      showToast("Failed to load submissions", "error");
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, debouncedSearch, typeFilter, creatorFilter, sort, page, pageSize]);

  // Populate the Type / Creator dropdowns once from a wide facet fetch.
  useEffect(() => {
    (async () => {
      try {
        const res = await submissionApi({}, 1000, 1);
        const data = Array.isArray(res?.data?.data) ? res.data.data : [];
        setContentTypes([
          "All",
          ...Array.from(
            new Set(data.map((d) => d["content-type"]).filter((v) => v && v !== "—"))
          ),
        ]);
        setCreators([
          "All",
          ...Array.from(
            new Set(data.map((d) => d.creator?.name).filter(Boolean))
          ),
        ]);
      } catch (e) {
        // Non-fatal: dropdowns just fall back to "All".
      }
    })();
  }, []);

  // Reset to page 1 whenever the view (tab/filter/sort/search) changes.
  useEffect(() => {
    setPage(1);
    setSelected(new Map());
  }, [tab, debouncedSearch, typeFilter, creatorFilter, sort, pageSize]);

  // Numeric-ID search is a client-side convenience on top of the server rows.
  const pageRows = useMemo(() => {
    const q = debouncedSearch.trim();
    if (q && /^\d+$/.test(q)) {
      // A series row matches by any episode it stands for, too.
      return rows.filter(
        (r) => String(r.id).includes(q) || r.episodeIds.some((id) => String(id).includes(q))
      );
    }
    return rows;
  }, [rows, debouncedSearch]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const toggleRow = (item) => {
    setSelected((prev) => {
      const next = new Map(prev);
      const key = rowKey(item);
      next.has(key) ? next.delete(key) : next.set(key, item);
      return next;
    });
  };
  const clearSelection = () => setSelected(new Map());

  // A season row opens the series page scoped to that season, so a decision
  // made there covers the same episodes as one made from this row.
  const openReview = (item) => {
    if (!item.seriesId) {
      navigate(`/mainlayout/submissions/${item.id}`);
      return;
    }
    const params = new URLSearchParams({ qc: item.qcStatus });
    if (item.seasonNumber != null) params.set("season", String(item.seasonNumber));
    navigate(`/mainlayout/submissions/series/${item.seriesId}?${params}`);
  };

  const doApprove = async ({ notify, trustCreator }) => {
    if (!approveTarget) return;
    const list = approveTarget.rows;
    setBusyId(list.length === 1 ? rowKey(list[0]) : "bulk");
    try {
      await applyDecision(
        list,
        {
          status: "Approved",
          notify,
          // Never send this on a bulk approve: a selection can span several
          // creators, so one checkbox would silently trust all of them. The
          // dialog already hides the option for a multi-row target; this
          // keeps the invariant in the code that sends the request rather
          // than relying on that UI flag alone.
          trust_creator: list.length === 1 ? !!trustCreator : false,
        },
        {
          verb: "approved",
          fallback: "Failed to approve",
          // Surface the API's message on a single approve so "…will now publish
          // without review" reaches the admin here too, not only on the detail page.
          success: (results) =>
            list.length > 1
              ? `${list.length} submissions approved`
              : results[0]?.data?.message || "Submission approved",
          close: () => setApproveTarget(null),
        }
      );
    } finally {
      setBusyId(null);
    }
  };

  const doReject = async ({ reason, note, notify, removeTrust }) => {
    if (!rejectTarget) return;
    const list = rejectTarget.rows;
    setRejecting(true);
    try {
      await applyDecision(
        list,
        {
          status: "Rejected",
          reason,
          note,
          notify,
          // Takedown only, single item only — never on a bulk reject, where
          // the selection can span several creators.
          remove_trust:
            rejectTarget.takedown && list.length === 1 ? !!removeTrust : false,
        },
        {
          verb: "rejected",
          fallback: "Failed to reject",
          success: () =>
            rejectTarget.takedown
              ? "Taken down — this content is no longer live"
              : list.length > 1
              ? `${list.length} submissions rejected`
              : "Submission rejected",
          close: () => setRejectTarget(null),
        }
      );
    } finally {
      setRejecting(false);
    }
  };

  const stats = [
    { label: "Total", value: counts.All, cls: "" },
    { label: "Pending Review", value: counts.Pending, cls: style.warning },
    { label: "Approved", value: counts.Approved, cls: style.success },
  ];

  return (
    <div className={style.page}>
      <div className={style.header}>
        <h1 className={style.heading}>Submissions</h1>
        <span className={style.headingCount}>{counts.All} total</span>
      </div>

      <div className={style.stats}>
        {stats.map((s) => (
          <div className={style.statCard} key={s.label}>
            <div className={style.statLabel}>{s.label}</div>
            <div className={`${style.statValue} ${s.cls}`}>{s.value}</div>
          </div>
        ))}
      </div>

      <div className={style.tabs}>
        {TABS.map((t) => (
          <button
            key={t}
            className={`${style.tab} ${tab === t ? style.tabActive : ""}`}
            onClick={() => setTab(t)}
          >
            {t}
            <span className={style.tabBadge}>{counts[t]}</span>
          </button>
        ))}
      </div>

      <div className={style.toolbar}>
        <div className={style.searchWrap}>
          <FiSearch className={style.searchIcon} size={16} />
          <input
            className={style.searchInput}
            placeholder="Search by title, creator, or ID"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          className={style.select}
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
        >
          {contentTypes.map((t) => (
            <option key={t} value={t}>
              {t === "All" ? "Type: All" : t}
            </option>
          ))}
        </select>
        <select
          className={style.select}
          value={creatorFilter}
          onChange={(e) => setCreatorFilter(e.target.value)}
        >
          {creators.map((c) => (
            <option key={c} value={c}>
              {c === "All" ? "Creator: All" : c}
            </option>
          ))}
        </select>
        <select
          className={style.select}
          value={sort}
          onChange={(e) => setSort(e.target.value)}
        >
          <option value="oldest">Sort: Oldest first</option>
          <option value="newest">Sort: Newest first</option>
          <option value="az">Sort: A–Z</option>
        </select>
      </div>

      {canDecide && selected.size > 0 && (
        <div className={style.bulkBar}>
          <span className={style.bulkCount}>{selected.size} selected</span>
          <button
            className={style.bulkBtn}
            disabled={busyId === "bulk"}
            onClick={() =>
              setApproveTarget({
                rows: [...selected.values()],
                title: `${selected.size} submissions`,
              })
            }
          >
            Approve selected
          </button>
          <button
            className={style.bulkBtn}
            onClick={() =>
              setRejectTarget({
                rows: [...selected.values()],
                title: `${selected.size} submissions`,
              })
            }
          >
            Reject selected
          </button>
          <button className={style.bulkClear} onClick={clearSelection}>
            Clear
          </button>
        </div>
      )}

      {loading ? (
        <div className={style.stateBox}>Loading submissions…</div>
      ) : pageRows.length === 0 ? (
        <div className={style.stateBox}>No submissions in “{tab}”.</div>
      ) : (
        <div className={style.rows}>
          {pageRows.map((item) => {
            const isPending = item.qcStatus === "Pending";
            const key = rowKey(item);
            const sel = selected.has(key);
            const rowBusy = busyId === key || busyId === "bulk";
            const submitted = fmtSubmitted(item.submittedAt);
            const meta = [
              item.seriesId
                ? `${item.seasonNumber != null ? item.name : "Series"} · ${plural(item.episodeCount, "episode")}`
                : episodeContext(item) || item.contentType,
              item.creator,
              submitted ? `submitted ${submitted}` : null,
              `SUB-${item.id}`,
            ]
              .filter(Boolean)
              .join(" · ");
            return (
              <div
                key={key}
                className={`${style.row} ${sel ? style.rowSelected : ""}`}
              >
                {isPending && canDecide ? (
                  <input
                    type="checkbox"
                    className={style.checkbox}
                    checked={sel}
                    onChange={() => toggleRow(item)}
                  />
                ) : (
                  <span style={{ width: 18, flexShrink: 0 }} />
                )}
                <Thumb src={item.media} alt={item.name} />
                <div className={style.rowInfo} onClick={() => openReview(item)}>
                  <div className={style.rowTitle}>{rowTitle(item)}</div>
                  <div className={style.rowMeta}>{meta}</div>
                </div>
                {isPending && waitingLabel(item.submittedAt) && (
                  <span className={style.waiting}>{waitingLabel(item.submittedAt)}</span>
                )}
                <span className={`${style.badge} ${badgeClass(item.qcStatus)}`}>
                  {item.qcStatus}
                </span>
                <div className={style.rowActions}>
                  {isPending && canDecide && (
                    <>
                      <button
                        className={style.approveBtn}
                        disabled={rowBusy}
                        onClick={() =>
                          setApproveTarget({
                            rows: [item],
                            title: dialogTitle(item),
                            creator: item.creatorInfo,
                          })
                        }
                      >
                        <FiCheck size={13} /> Approve
                      </button>
                      <button
                        className={style.rejectBtn}
                        disabled={rowBusy}
                        onClick={() =>
                          setRejectTarget({ rows: [item], title: dialogTitle(item) })
                        }
                      >
                        <FiX size={13} /> Reject
                      </button>
                    </>
                  )}
                  {/* Already live. A trusted creator's uploads publish with no
                      review, so there has to be a one-click way to pull one. */}
                  {/* A live series is taken down per episode from its review page. */}
                  {item.qcStatus === "Approved" && !item.seriesId && canDecide && (
                    <button
                      className={style.rejectBtn}
                      disabled={rowBusy}
                      onClick={() =>
                        setRejectTarget({
                          rows: [item],
                          title: item.name,
                          takedown: true,
                          creator: item.creatorInfo,
                        })
                      }
                    >
                      <FiX size={13} /> Take down
                    </button>
                  )}
                  <button
                    className={style.eyeBtn}
                    title="Review"
                    onClick={() => openReview(item)}
                  >
                    <FiEye size={16} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!loading && total > 0 && (
        <div className={style.footer}>
          <span className={style.showing}>
            Showing {(page - 1) * pageSize + 1}–
            {Math.min(page * pageSize, total)} of {total}
          </span>
          <div className={style.pager}>
            <select
              className={style.select}
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
            >
              {PAGE_SIZES.map((n) => (
                <option key={n} value={n}>
                  {n} / page
                </option>
              ))}
            </select>
            <button
              className={style.pageBtn}
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <FiChevronLeft size={16} />
            </button>
            <span className={style.pageInfo}>
              {page} / {totalPages}
            </span>
            <button
              className={style.pageBtn}
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              <FiChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      <RejectModal
        open={!!rejectTarget}
        takedown={!!rejectTarget?.takedown}
        canRemoveTrust={!!rejectTarget?.creator?.is_trusted_creator}
        creatorName={rejectTarget?.creator?.name || ""}
        title={rejectTarget?.title}
        submitting={rejecting}
        onCancel={() => setRejectTarget(null)}
        onConfirm={doReject}
      />

      <ConfirmDialog
        open={!!approveTarget}
        tone="success"
        confirmLabel="Approve"
        title="Approve submission"
        message={
          approveTarget
            ? `Approve ${
                approveTarget.rows.length > 1
                  ? `${approveTarget.rows.length} submissions`
                  : `“${approveTarget.title}”`
              }? Once approved it becomes live-ready.`
            : ""
        }
        showNotify
        showTrustCreator={
          approveTarget?.rows.length === 1 &&
          !!approveTarget?.creator &&
          !approveTarget.creator.is_trusted_creator
        }
        trustCreatorLabel={`Trust ${
          approveTarget?.creator?.name || "this creator"
        } — publish their future content without review`}
        trustCreatorHelper="Their later uploads and edits go live immediately instead of waiting here. This submission is approved either way."
        submitting={busyId !== null}
        onCancel={() => setApproveTarget(null)}
        onConfirm={doApprove}
      />
    </div>
  );
};

export default Submissions;
